'use client';
/**
 * VX L4-0 — KdRegionBar: 지역 셀렉 «상시 노출» 바(시·도 → 시·군).
 *
 * 새 상태 체계가 아니다. 헤더의 GlobalRegionChip 과 «같은 것» 을 쓴다(src/lib/nav/region-scope.ts):
 *   · 현재 지역 = currentScope(경로 허브 · ?r=)            · 적용 URL = buildApplyHref(부동산 → 색인 허브 / 게시판·블로그·홈 → ?r=)
 *   · 기억 = writeRegionMemory(localStorage kd_nav_region_v1) · 칩 동기화 = REGION_MEMORY_EVENT · 필터 섬 알림 = emitRegionParam
 *   · 계측 = region_chip_apply / region_select_apply (variant·category = 'bar' — 기존 이벤트 이름 그대로, RULES#161)
 *
 * 칩과 «다른 점» 은 둘뿐이다:
 *   1) 바는 «URL 이 말하는 지역» 만 선택 표시한다. 기억(localStorage)만 있고 URL 이 전국이면 「전국」 이 선택이다 —
 *      상시 노출 바가 화면 내용과 다른 지역을 켜 두면 「지금 보는 목록이 어느 지역인가」가 거짓이 된다.
 *   2) 서버가 경로로 지역을 «이미 아는» 화면(/apt/region/부산 등)은 initialCode 로 넘겨 SSR 부터 선택 표시한다(같은 URL = 같은 HTML,
 *      방문자·쿠키 무관 → 캐시·봇 HTML 동일). 안 넘기면 서버 HTML 은 「전국」 이고 수화 뒤에 URL 을 읽는다.
 *
 * data = HeaderRegionData — (main)/layout 이 이미 만들어 내리는 값(NavDataContext · ClientShell). 새 조회 없음.
 */
import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import type { HeaderRegionData } from '@/lib/v3/header-region-match';
import { applyOutcome, buildApplyHref, currentScope, findScopeNode, orderTops, scopeContext, writeRegionMemory, type ScopeNode } from '@/lib/nav/region-scope';
import { emitRegionParam, REGION_PARAM_EVENT } from '@/lib/nav/use-region-param';
import { navTrack } from '@/lib/nav/nav-track';
import { trackRx } from '@/lib/rx/track';
import KdRegionBarView from './KdRegionBarView';

/** GlobalRegionChip 이 듣는 이벤트 이름(그 파일의 REGION_MEMORY_EVENT 와 같은 값 — 칩 CSS 를 끌어오지 않으려고 문자열로 둔다. 테스트가 일치를 잠근다). */
export const KD_REGION_MEMORY_EVENT = 'kd:region-memory';

export interface KdRegionBarProps {
  data: HeaderRegionData | null | undefined;
  /** 서버가 경로로 아는 지역 코드(regions.code). 없으면 SSR 은 「전국」. */
  initialCode?: string | null;
  className?: string;
}

export default function KdRegionBar({ data, initialCode = null, className }: KdRegionBarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const rootRef = useRef<HTMLDivElement>(null);
  // 첫 렌더 = 서버와 같은 값(initialCode 또는 전국). location 은 useEffect 뒤에만 읽는다(수화 불일치 방지).
  const [node, setNode] = useState<ScopeNode | null>(() => findScopeNode(data, initialCode));

  useEffect(() => {
    if (!data) return;
    const sync = () => {
      // 기억은 넘기지 않는다(null) — URL 이 말하는 지역만. 경로·?r= 어느 쪽도 아니면 initialCode, 그것도 없으면 전국.
      const cur = currentScope(data, pathname, window.location.search, null);
      setNode(cur.node ?? findScopeNode(data, initialCode));
    };
    sync();
    window.addEventListener('popstate', sync);
    window.addEventListener(REGION_PARAM_EVENT, sync);
    return () => { window.removeEventListener('popstate', sync); window.removeEventListener(REGION_PARAM_EVENT, sync); };
  }, [data, pathname, initialCode]);

  // 선택된 칩이 가로 스크롤 밖이면 안으로(페이지 세로 스크롤은 건드리지 않는다 — scrollIntoView 금지).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>('.kd-rb-l').forEach((list) => {
      const cur = list.querySelector<HTMLElement>('[aria-current="true"]');
      if (!cur) return;
      const left = cur.offsetLeft - list.offsetLeft;
      if (left < list.scrollLeft || left + cur.offsetWidth > list.scrollLeft + list.clientWidth) {
        list.scrollLeft = Math.max(0, left - 16);
      }
    });
  }, [node]);

  const tops = useMemo(() => orderTops(data?.tops ?? []), [data]);
  const topCode = node?.parent?.code ?? node?.code ?? null;
  const leafCode = node?.parent ? node.code : null;
  const leaves = useMemo(() => (topCode ? tops.find((t) => t.code === topCode)?.children ?? [] : []), [tops, topCode]);

  const onPick = useCallback((code: string | null) => {
    const next = code ? findScopeNode(data, code) : null;
    const ctx = scopeContext(pathname);
    navTrack('region_chip_apply', { variant: 'bar', code: next?.code ?? 'national', ctx });
    const href = buildApplyHref(pathname, next);
    // nav_region1 — 결과·목적지(GlobalRegionChip 과 같은 규격).
    const out = applyOutcome(pathname, href, window.location.pathname + window.location.search);
    trackRx('region_select_apply', next?.code ?? 'national', `bar:${out.result}`, { target: out.target });
    writeRegionMemory(next?.code ?? null, next);
    setNode(next);
    try { window.dispatchEvent(new Event(KD_REGION_MEMORY_EVENT)); } catch { /* 무시 */ }
    if (!href) return;
    router.push(href);
    // 같은 경로에서 ?r= 만 바뀌면 필터 섬이 다시 마운트되지 않는다 → 알림(칩과 같은 처리).
    if (ctx === 'filter') emitRegionParam(next?.code ?? null);
  }, [data, pathname, router]);

  if (!data || !data.tops.length) return null;

  return (
    <div ref={rootRef} className="kd-rb-wrap">
      <KdRegionBarView
        tops={tops.map((t) => ({ code: t.code, label: t.label, count: t.count }))}
        leaves={leaves.map((l) => ({ code: l.code, label: l.label, count: l.count }))}
        topCode={topCode}
        leafCode={leafCode}
        onPick={onPick}
        className={className}
      />
    </div>
  );
}
